import { navigate, PageProps } from 'gatsby';
import React from 'react';
import { useGetMyAccountsQuery } from '~/graphql/types';
import SetupAccount from '~/scenes/Auth/SetupAccount';
import MainApp from '~/MainApp';
import Loading from '~/components/Loading';
import withErrorBoundary from '~/ErrorBoundary';
import ErrorScreen from '~/scenes/ErrorScreen';
import AccountLocked from '~/scenes/AccountStatus/AccountLocked';
import AccountCancelled from '~/scenes/AccountStatus/AccountCancelled';
import ErrorTypes from '~/ErrorTypes';
import { getErrorTypes } from '~/util/errorHandling';
import AccountPaused from '~/scenes/AccountStatus/AccountPaused';
import getCurrentAccount from '~/util/getCurrentAccount';
import DHRouter from '~/components/DHRouter';

type Props = PageProps;

/**
 * Take care of the authentication and account routing
 */
const AccountSelectionWrapper: React.FC<Props> = () => {
  const { data, loading, error, refetch } = useGetMyAccountsQuery();

  if (loading) return <Loading />;

  if (error) {
    /**
     * When a user gets deleted while still active inside the app
     * we get a `deletedUserError`. Redirect them to the logout page
     */
    const errorTypes = getErrorTypes(error);
    if (errorTypes.includes(ErrorTypes.deletedUserError)) {
      void navigate('/logout', {
        replace: true,
        state: { isUserDeleted: true },
      });

      return null;
    } else {
      return <ErrorScreen />;
    }
  }

  const account = getCurrentAccount(data);
  if (account == null) return <SetupAccount refetchAccounts={refetch} />;

  /* Keep Logout on top so it is accessible on SetupAccount as well */
  return (
    <DHRouter basepath="/-">
      <AccountLocked path="/locked" />
      <AccountPaused path="/paused" />
      <AccountCancelled path="/cancelled" />
      <MainApp default accountId={account.id} />
    </DHRouter>
  );
};

export default withErrorBoundary(AccountSelectionWrapper, <ErrorScreen />);
